import { ProductDataShape } from './ProductDataShape'
import { getLocalStorageSavedState } from './state/LocalStorageSavedState'
import { removeListItemsIfNotInProductData } from './state/removeListItemsIfNotInProductData'
import { DefaultListState } from './state/getDefaultAppState'
import { DeepLinkData } from './configurator/resolveDeeplinkProduct'
import { AppState } from './state/AppState'
import { UserType } from './types/userTypes'
import { logDebug } from './debug/debugLogging'
import { ProductSeries } from './types/productTypes'
import { ENABLE_SIMULATED_LOGIN_FLOW } from './environment'

/**
 * tries to load saved user state from local storage, validate that products exist, and combined with default UI state
 * @param productData
 * @param isAuthenticated
 * @param canLoadLocalStorageData
 * @param defaultState
 */
function loadInitialAppState(productData: ProductDataShape, isAuthenticated: boolean, canLoadLocalStorageData: boolean, defaultState: AppState): AppState {
  const localStorageSavedState = canLoadLocalStorageData ? getLocalStorageSavedState() : null

  if (localStorageSavedState) {
    localStorageSavedState.configuredProducts = removeListItemsIfNotInProductData(localStorageSavedState.configuredProducts, productData)
  }

  if (localStorageSavedState?.isAuthenticated && !isAuthenticated) {
    return defaultState
  }

  if (localStorageSavedState) {
    defaultState = {
      ...defaultState,
      userState: localStorageSavedState,
    }
  }

  return defaultState
}

/**
 * Factory-factory for initial application state, handling product deeplinks,
 *  login status {placeholder}, and application mode, e.g. wholesaler
 * @param products
 * @param defaultState
 * @param config
 */
export function getInitialAppStateFactory(
  products: ProductDataShape,
  defaultState: AppState,
  config: {
    deepLinkData: DeepLinkData | null
    isAuthenticated: boolean
    clearList: boolean
    overrideUserType: UserType | null
    overrideProductSeries: ProductSeries | null
    canLoadLocalStorageData: boolean
  },
) {
  const {
    deepLinkData,
    isAuthenticated,
    clearList,
    overrideUserType,
    overrideProductSeries,
    canLoadLocalStorageData,
  } = config

  return function getInitialAppState(): Readonly<AppState> {
    const initialState = loadInitialAppState(products, isAuthenticated, canLoadLocalStorageData, defaultState)

    if (overrideUserType) {
      initialState.userState.userType = overrideUserType
    }

    if (overrideProductSeries) {
      initialState.userState.selectedProductSeries = overrideProductSeries
    }

    // authenticated users are always electricians
    if (isAuthenticated) {
      initialState.userState.userType = UserType.Electrician
    }

    if (deepLinkData) {
      if (deepLinkData.skinProductOption.pro) {
        logDebug('getInitialAppState', 'initial state: changing to electrician due to pro product deep link')
        initialState.userState.selectedProductSeries = ProductSeries.Pro
        initialState.userState.userType = UserType.Electrician
      } else {
        if (initialState.userState.userType === UserType.Electrician) {
          logDebug('getInitialAppState', 'initial state: Iconic product deep link - changing selected range to Iconic')
          initialState.userState.selectedProductSeries = ProductSeries.Iconic
        }
      }
    }

    initialState.userState.isAuthenticated = isAuthenticated

    if (!ENABLE_SIMULATED_LOGIN_FLOW) {
      initialState.userState.isAuthenticated = false
    }

    // reset user's list
    if (clearList) {
      logDebug('getInitialAppState', 'initial state: Clearing list')

      initialState.userState = {
        ...initialState.userState,
        ...DefaultListState,
      }
    }

    return initialState
  }
}